import {
  $ProcedureControlStates,
  $ProcedureTraining,
  FetchAllTrainingProgramsCompany,
} from "@store/company/procedure-training-store";
import { AllProgramInternalStudy } from "@interfaces/company/procedure";
import ProcedureService from "@services/programs-service";
import { $UserAddPermissions, ACTIONS } from "@store/user-store";
import { Checkbox, Tooltip } from "@mui/material";
import { ErrorIndicator, Loader } from "@ui/indicators";
import { NotePencilIcon, TrashIcon } from "@phosphor-icons/react";
import React, { useEffect, useState } from "react";

import { WrapperSVG } from "@components/WrapperSVG/WrapperSVG";
import clsx from "clsx";
import style from "@scss/pages/company/departments.module.scss";
import tableStyle from "@scss/components/tables/base-table.module.scss";
import { useAsyncConfirm } from "@hooks/useAsyncConfirm";
import { useChoiceState } from "@hooks/useChoiceState";
import { useCompanyId } from "@store/company/company-store";
import useModal from "@modals/modal-hook";
import { useUnit } from "effector-react";

const InternalStudyProgramTable: React.FC = () => {
  const { isLoading, error, isFetched } = useUnit($ProcedureControlStates);
  const directionLearning = useUnit($ProcedureTraining);
  const permissions = useUnit($UserAddPermissions);
  const companyId = useCompanyId();

  const [programs, setPrograms] = useState<AllProgramInternalStudy[]>([]);

  const {
    selectedItems,
    areAllItemsSelected,
    selectAll,
    selectById,
    clearSelectedItems,
    getIsItemSelected,
  } = useChoiceState(programs, (program) => program.id);

  const { open } = useModal();
  const confirm = useAsyncConfirm();

  const handleRemoveProgram = (program: AllProgramInternalStudy) => {
    open("ConfirmActionModal", {
      btnText: "Удалить",
      modalData: {
        text: `Вы уверены, что хотите удалить программу "${program.title}"?`,
      },
      onConfirm: () => {
        ProcedureService.DeleteProgramStrict(
          program?.id,
          "training",
          (err, res) => {
            if (err || !res) {
              return Error(
                "При редактировании программы обучения произошла ошибка"
              );
            }
            if (res.data) {
              FetchAllTrainingProgramsCompany(companyId);
            }
          }
        );
      },
    });
  };

  const handleDeleteSelectedPrograms = async () => {
    const ok = await confirm({
      modalData: {
        text: "Вы уверены, что хотите удалить выбранные программы?",
      },
    });
    if (!ok) return;

    for (const program of selectedItems) {
      ProcedureService.DeleteProgramStrict(
        program?.id,
        "training",
        (err, res) => {
          if (err || !res) {
            return Error(
              "При ркдактировании программы обучения произошла ошибка"
            );
          }
        }
      );
    }

    FetchAllTrainingProgramsCompany(companyId);
    clearSelectedItems();
  };

  useEffect(() => {
    if (!isFetched && companyId !== null) {
      FetchAllTrainingProgramsCompany(companyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const programs: AllProgramInternalStudy[] = [];
    directionLearning.programs
      ?.filter((e) => e.programs.length)
      .forEach((category) => {
        category.programs.forEach((program) => {
          programs.push({
            id: program.id,
            categoryId: program.categoryId,
            period: program.period,
            termPrimaryConducting: program.termPrimaryConducting,
            title: program.title,
            category: category.title,
          });
        });
      });
    return setPrograms(programs);
  }, [directionLearning.programs]);

  const tableBodyContent = programs.length ? (
    programs.map((program) => {
      const isSelected = getIsItemSelected(program);
      const modalData = {
        editMode: true,
        modalTitle: "Изменить программу",
        programId: program.id,
        title: program.title,
        training: program.categoryId,
        termPrimaryConducting: program.termPrimaryConducting,
        period: program.period,
      };
      return (
        <tr key={program.id}>
          <td>
            <span>
              <Checkbox
                color="primary"
                checked={isSelected}
                onChange={(e) => selectById(program, e.target.checked)}
              />
            </span>
            <span
              className={style.wrapperRow}
              onClick={() =>
                open("CreateProgramModal", {
                  btnText: "Сохранить",
                  modalData: modalData,
                })
              }
            >
              <label
                htmlFor={`key`}
                className={clsx(
                  tableStyle.column_fixed_height,
                  style.wrapperLabel
                )}
              >
                <span className={style.wrapperLabel}>{program.title}</span>
              </label>
            </span>
          </td>
          <td align="left">{program.category}</td>
          <td align="left">{program.termPrimaryConducting}</td>
          <td align="left">{program.period}</td>
          <td>
            <div className={clsx(style.department_actions_col)}>
              {permissions.hasPermission(
                ACTIONS.educationalCenter_internalTraining_programs_allowedToEdit
              ) && (
                <WrapperSVG color={"orange"}>
                  <Tooltip title="Изменить" placement="top">
                    <NotePencilIcon
                      size={24}
                      className={style.department_actions_col__icon}
                      onClick={() =>
                        open("CreateProgramModal", {
                          btnText: "Сохранить",
                          modalData: modalData,
                        })
                      }
                    />
                  </Tooltip>
                </WrapperSVG>
              )}
              {permissions.hasPermission(
                ACTIONS.educationalCenter_internalTraining_programs_allowedToDelete
              ) && (
                <Tooltip title="Удалить" placement="top">
                  <TrashIcon
                    size={24}
                    className={style.department_actions_col__icon}
                    onClick={() => handleRemoveProgram(program)}
                  />
                </Tooltip>
              )}
            </div>
          </td>
        </tr>
      );
    })
  ) : (
    <tr>
      <td>
        <p style={{ textAlign: "center", width: "100%", margin: "24px 0px" }}>
          Программы не добавлены
        </p>
      </td>
    </tr>
  );

  return (
    <div
      className={clsx(
        tableStyle.base_table_container,
        style.departments_table,
        style.no_border_top
      )}
    >
      {isLoading ? (
        <Loader />
      ) : error ? (
        <ErrorIndicator />
      ) : (
        <table className={clsx(tableStyle.base_table)}>
          <thead>
            <tr>
              <td>
                <span>
                  <Checkbox
                    color="primary"
                    checked={areAllItemsSelected}
                    onChange={(e) => selectAll(e.target.checked)}
                  />
                </span>
                <label className={clsx(tableStyle.checkbox_label)}>
                  Название программы
                </label>
              </td>
              <td align="left">Категория</td>
              <td align="left">Первичное обучение</td>
              <td align="left">Повторное обучение</td>
              <td align="right">
                {selectedItems.length > 0 && (
                  <span>
                    {permissions.hasPermission(
                      ACTIONS.educationalCenter_internalTraining_programs_allowedToDelete
                    ) && (
                      <Tooltip title="Удалить" placement="top">
                        <TrashIcon
                          size={24}
                          className={style.department_actions_col__icon}
                          onClick={handleDeleteSelectedPrograms}
                        />
                      </Tooltip>
                    )}
                  </span>
                )}
              </td>
            </tr>
          </thead>
          <tbody>{tableBodyContent}</tbody>
        </table>
      )}
    </div>
  );
};

export default InternalStudyProgramTable;
